import { Component, effect, inject } from '@angular/core';
import {CommonTableConfig} from '@lib-ui/common-table/models/common-table-config.class';
import {OrderTableService} from './order-table.service';
import {LoaderComponent} from '@lib-ui/loader/loader.component';
import {CommonTableComponent} from '@lib-ui/common-table/ui/common-table.component';
import {ICommonTableAction, ICommonTableConfig} from '@lib-ui/common-table/models/common-table.interface';
import {RowAction} from '@lib-ui/common-table/models/common-table.enum';
import {Router} from '@angular/router';
import {AdvancedSearchComponent} from '@lib-ui/advanced-search/ui/advanced-search.component';
import {FilterConfig} from '@lib-ui/advanced-search/models/advanced-search.interface';
import {FormControl} from '@angular/forms';
import {OrderStatusEnum} from '@libs-enums/status.enum';
import {EnumService} from '@lib-services/enum.service';
import {ConditionsCheckEnum} from '@libs-enums/conditionsCheck.enum';
import {GroupsSelectorService} from '@lib-services/groups-selector.service';
import {filter, switchMap} from 'rxjs';
import {MessageService} from '@lib-services/message.service';
import {OrdersService} from '@lib-services/orders.service';
import { TimeZoneService } from '@lib-services/time-zone.service';

@Component({
    selector: 'app-order',
    standalone: true,
    imports: [
        CommonTableComponent,
        LoaderComponent,
        AdvancedSearchComponent,
    ],
    templateUrl: './order.component.html',
    styleUrl: './order.component.scss'
})
export class OrderComponent {
    tableConfig: ICommonTableConfig = new CommonTableConfig( {
        columns: [
            { name: 'id', title: 'ИН' },
            { name: 'executorName', title: 'Исполнитель' },
            { name: 'createdAt', title: 'Дата создания', type: Date },
            { name: 'dateFrom', title: 'Дата исполнения', type: Date },
            { name: 'workTime', title: 'Время исполнения' },
            { name: 'groupName', title: 'Группа' },
            { name: 'projectName', title: 'Проект' },
            { name: 'orderStatus', title: 'Статус' },
            { name: 'conditionsCheck', title: 'Проверка' },
            { name: 'address', title: 'Адрес', showToolTip: true },
            { name: 'discount', title: 'Скидка' },
        ],
        createButtonLabel: 'Добавить заявку',
        showCreateButton: true,
        withCheckboxes: true,
        rowActions: [RowAction.DELETE],
    });
    filterConfig: FilterConfig[];

    constructor(
        protected orderTableService: OrderTableService,
        private router: Router,
        private enumService: EnumService,
        private messageService: MessageService,
        private ordersService: OrdersService,
        private timeZoneService: TimeZoneService
    ) {
        this.filterConfig = [
            {
                type: 'input',
                label: 'ИН заявки',
                name: 'id',
            },
            {
                type: 'input',
                label: 'Фамилия исполнителя',
                name: 'executor.lastName',
            },
            {
                type: 'datepicker',
                label: 'Дата создания',
                name: 'createdAt',
            },
            {
                type: 'datepicker',
                label: 'Дата исполнения',
                name: 'dateTo',
            },
            {
                type: 'dropdown',
                label: 'Статус',
                name: 'status',
                options: this.enumService.enumTranslateStatusToArrSelects(OrderStatusEnum),
            },
            {
                type: 'common-dropdown',
                label: 'Группа',
                name: 'group.id',
                commonDropdownConfig: {
                    service: inject(GroupsSelectorService),
                    formCtrl: new FormControl(),
                },
            },
            {
                type: 'dropdown',
                label: 'Проверка',
                name: 'conditionsCheck',
                options: this.enumService.enumTranslateConditionsCheckToArrSelects(ConditionsCheckEnum),
            },
            {
                type: 'input',
                label: 'Адрес',
                name: 'addressDetails',
            },
        ];

        effect(() => {
            if (this.timeZoneService.currentTimeZone()) {
                this.applyFilters(this.tableConfig.filters as Record<string, string | null>);
            }
        }, { allowSignalWrites: true });
    }

    applyAction(value: ICommonTableAction): void {
        if (value.action === RowAction.CLICK) {
            this.router.navigate(['order-editor', value.data[0]['id']]);
        }
        if (value.action === RowAction.CREATE) {
            this.router.navigate(['order-editor']);
        }
        if (value.action === RowAction.DELETE) {
            const ids = value.data.map(el => Number(el['id']));
            this.deleteOrders(ids);
        }
    }

    applyFilters(filterData: Record<string, string | null>): void {
        this.tableConfig.filters = filterData;
        this.tableConfig.paginatorConfig.pageIndex = 0;
        this.orderTableService.updateData(this.tableConfig);
    }

    deleteOrders(ids: number[]): void {
        this.messageService.confirmMessage({
            title: 'Удаление заявки(ок)',
            text: 'Вы действительно хотите удалить выбранные заявки?',
            primaryText: 'Удалить',
            secondaryText: 'Отмена',
            showSecondaryButton: true,
        }).pipe(
            filter(result => result),
            switchMap(() => {
                return this.ordersService.deleteOrders(ids);
            }),
        ).subscribe({
            next: () => {
                this.messageService.successMessage('Заявки успешно удалены');
                this.orderTableService.updateData(this.tableConfig);
            },
            error: () => this.messageService.errorMessage('При удалении произошла ошибка')
        });
    }

}
